import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, X, Edit3, UploadCloud, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const empty = {
  title: "",
  slug: "",
  subtitle: "",
  description: "",
  features: [],
  price: 99,
  price_yearly: "",
  currency: "USD",
  image_url: "",
  download_asset_url: "",
  license_type: "account_bound",
  status: "active",
  category_slug: "",
};

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/admin/categories")]);
      setRows(p.data);
      setCats(c.data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!confirm(`Delete product "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [rechecking, setRechecking] = useState(false);
  const recheckLinks = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/admin/recheck-links");
      if (!data.checked) {
        toast.info("No external download links to check — your products use managed/internal storage or have no download link set.");
      } else if (data.broken > 0) {
        toast.warning(`${data.broken} of ${data.checked} external link(s) broken — ${data.checked - data.broken} OK. Check the flagged products.`);
      } else {
        toast.success(`All ${data.checked} external link(s) OK.`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRechecking(false); }
  };

  return (
    <div data-testid="admin-products">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Catalog</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Products</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{rows.length} product{rows.length === 1 ? "" : "s"}.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={recheckLinks} disabled={rechecking} data-testid="admin-prod-recheck-links" className="btn-edge-ghost text-sm">
            {rechecking ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-check links
          </button>
          <button
            onClick={() => setEditing({ ...empty, category_slug: cats[0]?.slug || "" })}
            data-testid="admin-prod-new"
            disabled={cats.length === 0}
            className="btn-edge text-sm disabled:opacity-40"
          >
            <Plus size={14} /> New product
          </button>
        </div>
      </div>

      <div className="card-edge overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
            <tr>
              <th className="px-5 py-3 font-medium">Title</th>
              <th className="px-5 py-3 font-medium">Category</th>
              <th className="px-5 py-3 font-medium">Price</th>
              <th className="px-5 py-3 font-medium">Version</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-emerald-100/50">No products yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-emerald-500/8 last:border-0 hover:bg-emerald-500/[0.03]">
                <td className="px-5 py-3.5 text-white">
                  <p>{p.title}</p>
                  <p className="mt-0.5 font-mono-edge text-[10px] text-emerald-100/40">{p.slug}</p>
                </td>
                <td className="px-5 py-3.5 text-emerald-100/70">{p.category_slug}</td>
                <td className="px-5 py-3.5 text-lime-300">${Number(p.price).toFixed(2)}</td>
                <td className="px-5 py-3.5 font-mono-edge text-[11px] text-emerald-100/60">v{p.version || "1.0.0"}</td>
                <td className="px-5 py-3.5">
                  <span className={`chip ${p.status === "active" ? "chip-emerald" : "chip-neutral"}`}>{p.status}</span>
                  {p.download_link_status && p.download_link_status.ok === false && (
                    <button onClick={() => setEditing({ ...p, features: p.features || [], _focusLink: true })}
                      className="chip ml-1 cursor-pointer border border-red-500/40 bg-red-500/15 text-red-300 transition-colors hover:bg-red-500/25"
                      title={`${p.download_link_status.note || "External download link failed the last check"} — click to fix`}
                      data-testid={`prod-link-broken-${p.id}`}>link broken</button>
                  )}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing({ ...p, features: p.features || [] })}
                            data-testid={`admin-prod-edit-${p.id}`}
                            className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white">
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`admin-prod-delete-${p.id}`}
                            className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-red-500/40 hover:text-red-300">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <ProductModal
          item={editing}
          cats={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductModal({ item, cats, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;

  const addFeature = () => {
    const v = featureInput.trim();
    if (!v) return;
    setForm({ ...form, features: [...(form.features || []), v] });
    setFeatureInput("");
  };
  const removeFeature = (i) => setForm({ ...form, features: form.features.filter((_, ix) => ix !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        category_slug: form.category_slug,
        title: form.title,
        slug: form.slug || undefined,
        subtitle: form.subtitle || "",
        description: form.description || "",
        features: form.features || [],
        price: Number(form.price) || 0,
        price_yearly: form.price_yearly !== "" && form.price_yearly != null ? Number(form.price_yearly) : null,
        currency: form.currency || "USD",
        image_url: form.image_url || null,
        download_asset_url: form.download_asset_url || null,
        license_type: form.license_type || "account_bound",
        billing_period: form.billing_period || "one_time",
        status: form.status || "active",
      };
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        const { data } = await api.put(`/admin/products/${item.id}`, payload);
        if (data?.download_link_status && data.download_link_status.ok === false) {
          toast.warning(`Saved, but the link still looks broken: ${data.download_link_status.note || "check it"}`);
        } else {
          toast.success("Product updated");
        }
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
          <div>
            <p className="overline">{isNew ? "New" : "Edit"} product</p>
            <h3 className="mt-1 font-editorial text-xl text-white">{isNew ? "Add product" : form.title}</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="grid gap-4 overflow-y-auto p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <SelectField label="Category" v={form.category_slug} on={(v) => setForm({ ...form, category_slug: v })} options={cats.map((c) => ({ v: c.slug, l: c.name }))} testId="prod-cat" />
            <SelectField label="License type" v={form.license_type} on={(v) => setForm({ ...form, license_type: v })} options={[{ v: "account_bound", l: "Account-bound" }, { v: "one_time", l: "One-time" }]} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <SelectField label="Billing period" v={form.billing_period || "one_time"} on={(v) => setForm({ ...form, billing_period: v })} options={[{ v: "one_time", l: "One-time" }, { v: "monthly", l: "Monthly (subscription)" }, { v: "yearly", l: "Yearly (subscription)" }, { v: "both", l: "Monthly + Yearly (both)" }, { v: "lifetime", l: "Lifetime" }]} testId="prod-billing" />
            <Field label={form.billing_period === "both" ? "Monthly price" : "Price"} v={form.price} on={(v) => setForm({ ...form, price: v })} type="number" testId="prod-price" />
          </div>
          {form.billing_period === "both" && (
            <Field label="Yearly price" v={form.price_yearly} on={(v) => setForm({ ...form, price_yearly: v })} type="number" testId="prod-price-yearly" />
          )}
          <Field label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} required testId="prod-title" />
          <Field label="Slug (optional)" v={form.slug} on={(v) => setForm({ ...form, slug: v })} />
          <Field label="Subtitle" v={form.subtitle} on={(v) => setForm({ ...form, subtitle: v })} />
          <Field label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} textarea testId="prod-desc" />
          <div>
            <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Features</label>
            <div className="grid gap-2">
              {(form.features || []).map((f, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white">
                  {f}
                  <button type="button" onClick={() => removeFeature(i)} className="text-emerald-100/50 hover:text-red-300"><X size={12} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="Add feature and Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                  className="flex-1 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
                <button type="button" onClick={addFeature} className="btn-edge-ghost text-xs">Add</button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price" type="number" v={form.price} on={(v) => setForm({ ...form, price: v })} testId="prod-price" />
            <Field label="Currency" v={form.currency} on={(v) => setForm({ ...form, currency: v })} />
            <SelectField label="Status" v={form.status} on={(v) => setForm({ ...form, status: v })} options={[{ v: "active", l: "Active" }, { v: "draft", l: "Draft" }, { v: "archived", l: "Archived" }]} />
          </div>
          <Field label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} />
          <AssetUploader form={form} setForm={setForm} isNew={isNew} />
          <VersionPublisher form={form} setForm={setForm} isNew={isNew} />
          <button disabled={saving} data-testid="prod-save" className="btn-edge justify-center">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </form>
      </div>
    </div>
  );
}

function AssetUploader({ form, setForm, isNew }) {
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [linkCheck, setLinkCheck] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => {
    if (form._focusLink && urlRef.current) {
      urlRef.current.focus();
      urlRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [form._focusLink]);
  const checkLink = async () => {
    const url = (form.download_asset_url || "").trim();
    if (!url) return;
    setChecking(true); setLinkCheck(null);
    try {
      const { data } = await api.post("/admin/validate-download-link", { url });
      setLinkCheck(data);
      if (data.ok) toast.success("Link looks valid"); else toast.warning(data.note || "Link check failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setChecking(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/products/${form.id}/asset`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, download_asset_name: data.name, download_asset_size: data.size, download_asset_url: "" });
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  const removeAsset = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/products/${form.id}/asset`);
      setForm({ ...form, download_asset_name: null, download_asset_size: null, download_asset_url: "" });
      toast.success("File removed");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Downloadable file (.ex4/.ex5/.mq4/.mq5/.zip/.pdf/.set · max 200 MB)</label>
      {isNew ? (
        <p className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-xs text-emerald-100/60">Save the product first, then reopen it to attach a downloadable file.</p>
      ) : form.download_asset_name ? (
        <div className="flex items-center justify-between rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white">
          <span className="truncate" data-testid="prod-asset-name">{form.download_asset_name}{form.download_asset_size ? ` · ${(form.download_asset_size / 1048576).toFixed(1)} MB` : ""}</span>
          <button type="button" onClick={removeAsset} disabled={busy} data-testid="prod-asset-remove" className="text-emerald-100/60 hover:text-red-300"><X size={14} /></button>
        </div>
      ) : (
        <div className="grid gap-2">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-emerald-500/30 bg-[#0d2416]/40 px-3 py-3 text-sm text-emerald-100/70 hover:border-lime-400/50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {busy ? "Uploading…" : "Choose file to upload"}
            <input type="file" className="hidden" accept=".ex4,.ex5,.mq4,.mq5,.zip,.pdf,.set" onChange={upload} disabled={busy} data-testid="prod-asset-input" />
          </label>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-100/40"><span className="h-px flex-1 bg-emerald-500/15" />or link<span className="h-px flex-1 bg-emerald-500/15" /></div>
          <div className="flex gap-2">
            <input ref={urlRef} type="url" value={form.download_asset_url || ""} onChange={(e) => { setForm({ ...form, download_asset_url: e.target.value }); setLinkCheck(null); }}
              placeholder="External download link (Google Drive / Mega / direct URL)" data-testid="prod-asset-url"
              className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
            <button type="button" onClick={checkLink} disabled={checking || !(form.download_asset_url || "").trim()} data-testid="prod-asset-url-check" className="btn-edge-ghost shrink-0 text-xs">
              {checking ? <Loader2 size={14} className="animate-spin" /> : "Check link"}
            </button>
          </div>
          {linkCheck && (
            <p className={`text-[11px] ${linkCheck.ok ? "text-lime-300" : "text-amber-300"}`} data-testid="prod-asset-url-result">
              {linkCheck.ok ? "✓ " : "⚠ "}{linkCheck.note}{linkCheck.hint ? ` ${linkCheck.hint}` : ""}
            </p>
          )}
          {!linkCheck && form.download_link_status && (
            <p className={`text-[11px] ${form.download_link_status.ok ? "text-lime-300" : "text-amber-300"}`} data-testid="prod-asset-url-status">
              {form.download_link_status.ok ? "✓ Last auto-check OK" : `⚠ ${form.download_link_status.note || "Last auto-check failed"}`}
            </p>
          )}
          <p className="text-[11px] text-emerald-100/50">Buyers are redirected here only after purchase. For Google Drive use a direct link (<code>uc?export=download&amp;id=…</code>). Paste an <code>s3://bucket/key</code> link (MinIO/AWS/R2/Spaces) to hand buyers a short-lived <b>signed URL</b> that can't be re-shared. Other external links can be re-shared — upload to storage for un-shareable delivery.</p>
        </div>
      )}
    </div>
  );
}

function VersionPublisher({ form, setForm, isNew }) {
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const publish = async () => {
    const v = version.trim();
    if (!v) { toast.error("Enter a version number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/${form.id}/publish-update`, { version: v, notes: notes.trim() });
      setForm({ ...form, version: data.version });
      toast.success(`v${data.version} published · ${data.notified} owner${data.notified === 1 ? "" : "s"} notified`);
      setVersion(""); setNotes("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/40 p-4" data-testid="version-publisher">
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Software version &amp; updates</label>
        <span className="chip chip-neutral" data-testid="version-current">current v{form.version || "1.0.0"}</span>
      </div>
      {isNew ? (
        <p className="text-xs text-emerald-100/60">Save the product first, then publish version updates here to notify existing owners.</p>
      ) : (
        <div className="grid gap-2">
          <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="New version e.g. 1.1.0" data-testid="version-input"
            className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Release notes — what changed in this update" data-testid="version-notes"
            className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          <p className="text-[11px] text-emerald-100/50">Upload the new file above first, then publish the version to alert owners &amp; let them re-download.</p>
          <button type="button" onClick={publish} disabled={busy} data-testid="version-publish" className="btn-edge-ghost justify-center text-xs">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Publish update &amp; notify owners
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, textarea, type = "text", required, placeholder, testId }) {  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      {textarea ? (
        <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={4} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      ) : (
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      )}
    </div>
  );
}
function SelectField({ label, v, on, options, testId }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId}
        className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2.5 text-sm text-white">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
